import { getProfileIncomeBreakdown } from "../../helper/profileCalculations";
import {
  isApplicationGuarantor,
  resolveEffortRatePercentageWithDefault,
} from "../../helper/effortRateSettings";
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/auth/AuthContext";
import {
  calculateEffortRate,
  getUserDisplayName,
  haveValue,
  shouldDeductNextView,
  toast,
  convertCurrencyFormat,
  shouldShowCapacityReport,
} from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  calculateNowTime,
  getProfileReminders,
} from "../../services/sharedService";
import moment from "moment";
import LandLordContext from "../../context/landlord/LandLordContext";
import AddPropertyModal from "../../pages/landlord/profile-request/AddPropertyModal";
import NewGuarantorsModal from "../../pages/landlord/profile-request/NewGuarantorsModal";
import ServingPromptModal from "../../pages/landlord/profile-request/ServingPromptModal";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";
import DocsStatus from "./DocsStatus";
import { Avatar, Badge, Button, Icon } from "../design";
import FlatmateModal from "../TenantDetails/FlatmateModal";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { getSavedIncomeComponents } from "../../helper/payslipSelection";
import ClientIdentificationStatus, {
  isClientIdentificationPending,
} from "./ClientIdentificationStatus";

const ProfileRow = ({
  profile,
  properties,
  loadData,
  tenantChecks = false,
  clientIdentificationRule = false,
}) => {
  const { _t } = useTranslator();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const { workspaceId } = useParams();
  const { user, checkIeDetails, setLoading } = authContext;
  const landLordContext = useContext(LandLordContext);
  const { editSharedProfile, propertiesAll, deaWorkspace } = landLordContext;
  const navigate = useNavigate();
  const [linkedPropertiesModel, setLinkedPropertiesModel] = useState(false);
  const [defaultPropertyLink, setDefaultPropertyLink] = useState([]);
  const [selectBtn, setSelectBtn] = useState(_t("select") + "...");
  const [propertyLink, setPropertyLink] = useState([]);
  const [propertyLinkedLoading, setPropertyLinkedLoading] = useState(false);
  const [propertyChanged, setPropertyChanged] = useState(false);
  const [guarantorModel, setGuarantorModel] = useState(false);
  const [flatmateModel, setFlatmateModel] = useState(false);
  const [servingPromptModal, setServingPromptModal] = useState(false);
  const [reminderSentAt, setReminderSentAt] = useState(null);
  const availablePropertyIds = new Set(
    (propertiesAll ?? []).map((property) => property?._id?.toString())
  );
  const linkedPropertyCount = (propertyLink ?? []).filter((propertyId) =>
    availablePropertyIds.has(propertyId?.toString())
  ).length;

  const tenant = profile?.tenantid;
  const displayName = getUserDisplayName(tenant);
  const nameParts = displayName.split(" ").filter(Boolean);
  const initials = `${(nameParts[0]?.charAt(0) ?? "").toUpperCase()}${(nameParts[1]?.charAt(0) ?? "").toUpperCase()}`;
  const reminderTooltip = reminderSentAt
    ? `${_t("reminderSent", "Reminder sent")} ${moment(reminderSentAt)
        .locale(
          (localStorage.getItem("i18nextLng") || "").startsWith("es")
            ? "es"
            : "en-gb"
        )
        .fromNow()}`
    : undefined;

  useEffect(() => {
    if (haveValue(properties) && haveValue(profile)) {
      if (haveValue(profile.propertyid)) {
        const temp_arr = [];
        var result = profile.propertyid.map((id) => {
          const obj = properties.find((item) => item._id === id);
          if (obj) {
            temp_arr.push(
              `${obj.street} - ${
                Number.isNaN(parseInt(obj.street_number, 10))
                  ? "N/A"
                  : String(parseInt(obj.street_number, 10))
              }, ${obj.floor_number}-${obj.flat_number}`
            );
            return {
              value: obj?._id,
              label: `${obj.street} - ${
                Number.isNaN(parseInt(obj.street_number, 10))
                  ? "N/A"
                  : String(parseInt(obj.street_number, 10))
              }, ${obj.floor_number}-${obj.flat_number}`,
            };
          }
          return null; // Handle case where id is not found in array1
        });

        result = result.filter((val) => haveValue(val));

        var placeholder_string = [];

        temp_arr.forEach((val) => {
          placeholder_string.push(
            <span className="placeholder_string">{val}</span>
          );
        });
        if (placeholder_string.length == 0) setSelectBtn(_t("select") + "...");
        else setSelectBtn(placeholder_string);

        setPropertyLink(profile?.propertyid);
        setDefaultPropertyLink(result);
      }
    }
  }, [properties, profile]);

  useEffect(() => {
    const isEqual =
      JSON.stringify([...(propertyLink ?? [])].map((i) => i).sort()) ===
      JSON.stringify(
        [...(defaultPropertyLink ?? [])].map((i) => i.value).sort()
      );

    setPropertyChanged(!isEqual);
  }, [propertyLink, defaultPropertyLink, linkedPropertiesModel]);

  useEffect(() => {
    if (!tenant?.is_outdated || !tenant?._id) {
      setReminderSentAt(null);
      return;
    }
    let cancelled = false;
    getProfileReminders(tenant._id)
      .then((response) => {
        if (!cancelled) {
          setReminderSentAt(response?.data?.[0]?.sent_at || null);
        }
      })
      .catch(() => {
        if (!cancelled) setReminderSentAt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenant?.is_outdated, tenant?._id]);

  const refreshProfilesInBackground = () => {
    Promise.resolve(loadData()).catch((err) => {
      console.log("Error refreshing profiles after property update", err);
    });
  };

  const handleLinkProperty = async (
    propertyToAdd = null,
    propertyToRemove = null
  ) => {
    if (haveValue(propertyToAdd)) {
      const data = {
        property_id: propertyToAdd,
        status: "add",
      };
      setPropertyLinkedLoading(true);
      try {
        const response = await editSharedProfile(profile?._id, data);
        if (response) {
          setPropertyLink((currentIds) =>
            currentIds.some(
              (id) => id?.toString() === propertyToAdd?.toString()
            )
              ? currentIds
              : [...currentIds, propertyToAdd]
          );
          refreshProfilesInBackground();
        }
        return Boolean(response);
      } finally {
        setPropertyLinkedLoading(false);
      }
    } else if (haveValue(propertyToRemove)) {
      const data = {
        property_id: propertyToRemove,
        status: "remove",
      };
      setPropertyLinkedLoading(true);
      try {
        const response = await editSharedProfile(profile?._id, data);
        if (response) {
          setPropertyLink((currentIds) =>
            currentIds.filter(
              (id) => id?.toString() !== propertyToRemove?.toString()
            )
          );
          refreshProfilesInBackground();
        }
        return Boolean(response);
      } finally {
        setPropertyLinkedLoading(false);
      }
    } else {
      toast.error(_t("pleaseSelectPropertyToLink"));
    }
  };

  const handleUnlinkProperty = async (propertyId) => {
    await handleLinkProperty(null, propertyId);
  };

  const eachRentAmount = (fullinfo) => {
    const personId = fullinfo?.tenantid?._id;
    let profile_effort_rate_percentage = resolveEffortRatePercentageWithDefault(
      fullinfo?.effort_rate_data,
      personId,
      {
        isGuarantor: isApplicationGuarantor(fullinfo, personId),
        defaultEffortRate: user?.current_workspace?.default_effort_rate ?? 35,
      }
    );

    const { combinedIncome } = getProfileIncomeBreakdown(
      fullinfo?.tenantid,
      fullinfo,
      { user, _t, deaWorkspace }
    );

    const data = {
      ...fullinfo,
      rent_percentage: profile_effort_rate_percentage,
    };
    let temp_obj = calculateEffortRate(combinedIncome, user, data);

    return {
      combinedIncome,
      effort_rate: !Number.isNaN(temp_obj?.effort_amount)
        ? Math.round(Number(temp_obj.effort_amount))
        : 0,
      profile_effort_rate_percentage,
    };
  };

  const handleOpenProfile = async () => {
    try {
      if (
        tenantChecks &&
        profile?.tenantid?.profile_completed == false &&
        WORKSPACE_ROLES_CAPABILITIES?.tenantChecks?.edit?.includes(
          user?.current_workspace_role
        )
      ) {
        navigate(
          `/${workspaceId}/tenant-check/create?tenant_id=${profile?.tenantid?._id}`
        );
        return;
      }
      setLoading(true);
      const checkView = await shouldDeductNextView(user, profile?._id);
      if (checkView) {
        setServingPromptModal(true);
      } else {
        // user?.isParentLL
        //   ? navigate(
        //       `/${workspaceId}/profile-property?agencylink=${profile?._id}${
        //         tenantChecks ? "&profile_type=tenant_check" : ""
        //       }`
        //     )
        //   :
        navigate(
          `/${workspaceId}/profile-property?sharedprofileid=${profile?._id}${
            tenantChecks ? "&profile_type=tenant_check" : ""
          }`,
          {
            state: { from: location.pathname + location.search },
          }
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const isHighlight =
    profile?.opened_at &&
    profile?.sortDate &&
    new Date(profile.sortDate) > new Date(profile.opened_at);

  const isProfileCompleted =
    !profile?.pendingDecision &&
    !profile?.tenantid?.tenant_extra_field_id?.landlord_ids_for_forensic_notification?.some(
      (i) => i?.profile_id === profile?._id
    );

  const clientIdentificationPending = isClientIdentificationPending(
    tenant,
    clientIdentificationRule
  );

  const missingCount = profile?.tenantid?.missingCount || 0;

  const calculateScore = (userDetails) => {
    var result = 0;

    if (checkIeDetails(userDetails)) {
      var totalScore =
        (userDetails?.paymentScores?.creditScore?.value || 0) / 2;

      if (totalScore > 850) {
        totalScore = 850;
      }
      result = totalScore;
    } else {
      if (haveValue(userDetails?.paymentScores?.creditScore?.value)) {
        result = userDetails?.paymentScores?.creditScore?.value;
      }
    }

    return result;
  };
  const handleSkorProtect = () => {
    window.open(
      "https://www.skortorent.com/inquilino/skor-protect",
      "_blank",
      "noopener,noreferrer"
    );
  };
  return (
    <>
      <tr
        className={`hideOnMobile second-row relative ${
          isHighlight ? "highlightRow" : ""
        } ${tenant?.is_outdated ? "dimmedRow" : ""}`}
      >
        <td
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
          className={`${isProfileCompleted ? "cursor-pointer" : ""}`}
        >
          <div className="profile-cell-tenant">
            <Avatar name={displayName || "U"} size="sm" />
            <span className="profile-cell-name">{displayName}</span>
            {profile?.tenantid?.skor_protect && (
              <>
                <span
                  data-tooltip-id={`isf-sp-badge-tooltip-${profile?._id}`}
                  className="d-flex align-items-center"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <SkorProtectBadge />
                </span>
                <ReactTooltip
                  id={`isf-sp-badge-tooltip-${profile?._id}`}
                  place="bottom"
                  className="react-tooltip-styling react-tooltip-styling-skor-protect"
                  clickable
                  style={{
                    maxWidth: "20.625rem",
                    whiteSpace: "normal",
                  }}
                  render={() => (
                    <p
                      style={{
                        lineHeight: 1.2,
                      }}
                    >
                      {_t("skorProtectTooltipDescription")}{" "}
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSkorProtect();
                        }}
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {_t("skorProtectTooltipLink")}
                      </span>
                    </p>
                  )}
                />
              </>
            )}
          </div>
        </td>

        <td className={"skor-details-column"}>
          {calculateScore(profile?.tenantid)}
        </td>

        <td
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
          className={`${isProfileCompleted ? "cursor-pointer" : ""}`}
        >
          €{convertCurrencyFormat(eachRentAmount(profile)?.combinedIncome)}
        </td>

        <td
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
          className={`${isProfileCompleted ? "cursor-pointer" : ""}`}
        >
          €{convertCurrencyFormat(eachRentAmount(profile)?.effort_rate)}
        </td>

        {/* <td>
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                setFlatmateModel(true);
              }}
              className={`d-flex align-items-center bg-[#DBF1CA] cursor-pointer bg_test removeGreenBg`}
            >
              <div className="d-flex align-items-center custom-gap-2">
                <p className="linked_p">
                  {profile?.tenantid?.sharers?.length ?? 0}
                </p>
                <img className="" src="/images/arrow-right-green.svg" />
              </div>
            </div>
          </div>
        </td> */}

        <td>
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                if (
                  WORKSPACE_ROLES_CAPABILITIES?.tenants?.viewGuarantors?.includes(
                    user?.current_workspace_role
                  )
                ) {
                  setGuarantorModel(true);
                }
              }}
              className="profile-count-link"
            >
              {(profile?.tenantid?.gurantor_id ?? []).length}
              {WORKSPACE_ROLES_CAPABILITIES?.tenants?.viewGuarantors?.includes(
                user?.current_workspace_role
              ) && <Icon name="chevronRight" size={16} />}
            </div>
          </div>
        </td>

        <td>
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                setLinkedPropertiesModel(true);
              }}
              className="profile-count-link"
            >
              {linkedPropertyCount}
              <Icon name="chevronRight" size={16} />
              {WORKSPACE_ROLES_CAPABILITIES?.properties?.linkTenant?.includes(
                user?.current_workspace_role
              ) && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconStart={<Icon name="plus" size={14} />}
                >
                  {_t("add")}
                </Button>
              )}
            </div>
          </div>
        </td>

        {!tenantChecks && (
          <td>
            <DocsStatus missingCount={missingCount} />
          </td>
        )}

        <td
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
          className={`${isProfileCompleted ? "cursor-pointer" : ""}`}
        >
          <div className="d-flex custom-gap-2 align-items-center">
            {tenant?.is_outdated ? (
              <div
                className="d-flex flex-column"
                style={{ alignItems: "flex-start", gap: "2px" }}
              >
                <span title={reminderTooltip}>
                  <Badge tone={reminderSentAt ? "neutral" : "warning"}>
                    {reminderSentAt
                      ? _t("profileUpdateRequested", "Update requested")
                      : _t("outdated")}
                  </Badge>
                </span>
                {false && reminderSentAt && (
                  <span
                    className="profileReminderDate"
                    style={{ fontSize: "var(--text-11)" }}
                  >
                    {_t("reminderSent", "Reminder sent")}{" "}
                    {moment(reminderSentAt)
                      .locale(
                        (localStorage.getItem("i18nextLng") || "").startsWith(
                          "es"
                        )
                          ? "es"
                          : "en-gb"
                      )
                      .fromNow()}
                  </span>
                )}
              </div>
            ) : !isProfileCompleted ? (
              profile?.tenantid?.step <= 3 ? (
                <Badge tone="warning">{_t("pendingDocUpload")}</Badge>
              ) : profile?.tenantid?.step === 4 ? (
                <Badge tone="warning">{_t("pendingKyc")}</Badge>
              ) : profile?.pendingDecision ? (
                <Badge tone="warning">{_t("pendingDecisionFromTenant")}</Badge>
              ) : (
                <Badge tone="neutral" className="profile-badge-processing">
                  <span className="ds-spinner" aria-hidden="true" />
                  {_t("processingProfile")}
                </Badge>
              )
            ) : clientIdentificationPending ? (
              <ClientIdentificationStatus />
            ) : (
              <div
                className={`${
                  isHighlight ? "d-flex align-items-center custom-gap-2" : ""
                }`}
              >
                {calculateNowTime(
                  profile?.sortDate,
                  localStorage.getItem("i18nextLng")
                )}
              </div>
            )}
            {!tenant?.is_outdated &&
              !clientIdentificationPending &&
              isHighlight && <Badge>{_t("recent")}</Badge>}
          </div>
        </td>

        {isProfileCompleted ? (
          <td
            onClick={() => {
              handleOpenProfile();
            }}
          >
            <span className="profile-row-open">
              <Icon name="chevronRight" size={18} />
            </span>
          </td>
        ) : (
          <td></td>
        )}
      </tr>
      {/* Mobile Card Structure */}
      <div
        className={`table-mobile-card hideOnDesktop 
        ${isHighlight ? "highlightMobileCard" : ""} ${tenant?.is_outdated ? "dimmedMobileCard" : ""}`}
      >
        {/* Tenant */}
        <div
          className="d-flex justify-content-between gap-3 align-items-center mb-3 cursor-pointer"
          onClick={() => {
            if (isProfileCompleted) handleOpenProfile();
          }}
        >
          <div className="d-flex justify-content-start gap-3 align-items-center">
            <div
              className="earner-thumbnail-wrapper d-flex items-center justify-center mr-3"
              style={{ backgroundColor: "var(--lime)" }}
            >
              <span
                style={{
                  color: "var(--ink)",
                  fontWeight: 600,
                  fontSize: "var(--text-13)",
                }}
              >
                {initials || "U"}
              </span>
            </div>
            <div className="main-text d-flex flex-column gap-1">
              {getUserDisplayName(profile?.tenantid)}
              {profile?.tenantid?.skor_protect && (
                <>
                  <span
                    data-tooltip-id={`isf-sp-badge-tooltip-${profile?._id}`}
                    className="d-flex align-items-center mt-1"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {" "}
                    <SkorProtectBadge />
                  </span>
                  <ReactTooltip
                    id={`isf-sp-badge-tooltip-${profile?._id}`}
                    place="bottom"
                    className="react-tooltip-styling react-tooltip-styling-skor-protect"
                    clickable
                    style={{
                      maxWidth: "20.625rem",
                      whiteSpace: "normal",
                    }}
                    render={() => (
                      <p
                        style={{
                          lineHeight: 1.2,
                        }}
                      >
                        {_t("skorProtectTooltipDescription")}{" "}
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSkorProtect();
                          }}
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          {_t("skorProtectTooltipLink")}
                        </span>
                      </p>
                    )}
                  />
                </>
              )}
            </div>
          </div>
          <div className="d-flex justify-content-start gap-4 align-items-center">
            {!clientIdentificationPending && isHighlight && (
              <p className="recentText ml-2">{_t("recent")}</p>
            )}
            <div
              className="flex justify-end cursor-pointer"
              onClick={() => {
                if (isProfileCompleted) handleOpenProfile();
              }}
            >
              <img
                className="profile-row-arrow-btn"
                src="/images/arrow-right-green.svg"
              />
            </div>
          </div>
        </div>

        {/* Score */}
        <div className="table-mobile-card-comp">
          {" "}
          <div
            className="table-mobile-card-data cursor-pointer"
            onClick={() => {
              if (isProfileCompleted) handleOpenProfile();
            }}
          >
            <p className="text-gray-500">{_t("income")}</p>
            <span className="card-text">
              €{convertCurrencyFormat(eachRentAmount(profile)?.combinedIncome)}
            </span>
          </div>
          {/* Effort Rate */}
          <div
            className="table-mobile-card-data cursor-pointer"
            onClick={() => {
              if (isProfileCompleted) handleOpenProfile();
            }}
          >
            <p className="text-gray-500">{_t("effortRate")}</p>
            <span className="card-text">
              €{convertCurrencyFormat(eachRentAmount(profile)?.effort_rate)}
            </span>
          </div>
          {/* Flatmates */}
          <div className="table-mobile-card-data">
            <p className="text-gray-500">{_t("flatmates")}</p>
            <div
              onClick={() => setFlatmateModel(true)}
              className="d-flex align-items-center cursor-pointer"
            >
              <p className="card-text">
                {(profile?.tenantid?.sharers?.length ?? 0) +
                  (profile?.tenantid?.tenant_extra_field_id?.selected_onboarding_users?.filter(
                    (item) => item?.type === "sharer"
                  )?.length ?? 0)}
              </p>
              <img src="/images/arrow-right-green.svg" className="px-2" />
            </div>
          </div>
          {/* Guarantors */}
          <div className="table-mobile-card-data">
            <p className="text-gray-500">{_t("guarantors")}</p>
            <div
              onClick={() => setGuarantorModel(true)}
              className="d-flex align-items-center cursor-pointer"
            >
              <p className="card-text">
                {(profile?.tenantid?.gurantor_id ?? []).length}
              </p>
              <img src="/images/arrow-right-green.svg" className="px-2" />
            </div>
          </div>
          {/* Linked Properties */}
          <div className="table-mobile-card-data">
            <p className="text-gray-500">{_t("linkedProperties")}</p>
            <div
              onClick={() => setLinkedPropertiesModel(true)}
              className={`d-flex align-items-center cursor-pointer`}
            >
              <p className="card-text">{linkedPropertyCount}</p>
              <img src="/images/arrow-right-green.svg" className="px-2" />
              <div className="d-flex align-items-center card-add-btn cursor-pointer">
                <img src="/images/addGreen.svg" />
                <p className="">{_t("add")}</p>
              </div>
            </div>
          </div>
          {/* Docs */}
          {!tenantChecks && (
            <div className="table-mobile-card-data">
              <p className="text-gray-500">{_t("docs")}</p>
              <DocsStatus missingCount={missingCount} />
            </div>
          )}
          {/* Assign Agent / Landlord */}
          {/* {user?.isParentLL && (
            <div className="table-mobile-card-data">
              <p className="text-gray-500">{_t("assignAgent/Landlord")}</p>
              <div
                className="d-flex align-items-center cursor-pointer"
                onClick={() => openAssignLLModal(profile?.tenantid?._id)}
              >
                <span className="card-text">{profile.agent_count}</span>
                <img src="/images/arrow-right-green.svg" className="px-2" />

                <div className="d-flex align-items-center card-add-btn cursor-pointer">
                  <img src="/images/addGreen.svg" />
                  <p className="">{_t("add")}</p>
                </div>
              </div>
            </div>
          )} */}
          {/* Info Updated At */}
          <div
            className="table-mobile-card-data cursor-pointer"
            onClick={() => {
              if (isProfileCompleted) handleOpenProfile();
            }}
          >
            <p className="text-gray-500">{_t("infoUpdatedAt")}</p>
            {tenant?.is_outdated ? (
              <div
                className="d-flex flex-column"
                style={{ alignItems: "flex-start", gap: "2px" }}
              >
                <span title={reminderTooltip}>
                  <Badge tone={reminderSentAt ? "neutral" : "warning"}>
                    {reminderSentAt
                      ? _t("profileUpdateRequested", "Update requested")
                      : _t("outdated")}
                  </Badge>
                </span>
                {false && reminderSentAt && (
                  <span
                    className="profileReminderDate"
                    style={{ fontSize: "var(--text-11)" }}
                  >
                    {_t("reminderSent", "Reminder sent")}{" "}
                    {moment(reminderSentAt)
                      .locale(
                        (localStorage.getItem("i18nextLng") || "").startsWith(
                          "es"
                        )
                          ? "es"
                          : "en-gb"
                      )
                      .fromNow()}
                  </span>
                )}
              </div>
            ) : !isProfileCompleted ? (
              profile?.tenantid?.step <= 3 ? (
                <Badge tone="warning">{_t("pendingDocUpload")}</Badge>
              ) : profile?.tenantid?.step === 4 ? (
                <Badge tone="warning">{_t("pendingKyc")}</Badge>
              ) : profile?.pendingDecision ? (
                <Badge tone="warning">{_t("pendingDecisionFromTenant")}</Badge>
              ) : (
                <Badge tone="neutral" className="profile-badge-processing">
                  <span className="ds-spinner" aria-hidden="true" />
                  {_t("processingProfile")}
                </Badge>
              )
            ) : clientIdentificationPending ? (
              <ClientIdentificationStatus />
            ) : (
              <div className="d-flex align-items-center card-text">
                {calculateNowTime(
                  profile?.sortDate,
                  localStorage.getItem("i18nextLng")
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action (Arrow) */}
      </div>

      <AddPropertyModal
        isOpen={linkedPropertiesModel}
        onClose={() => setLinkedPropertiesModel(false)}
        properties={properties}
        profile={profile}
        linkedPropertyIds={propertyLink}
        handleUnlinkProperty={(id) => handleUnlinkProperty(id)}
        handleLinkProperty={handleLinkProperty}
      />
      <FlatmateModal
        user={profile?.tenantid}
        sharerData={
          profile?.sharersData ??
          profile?.tenantid?.sharers?.map((s) => ({
            ...s,
          })) ??
          []
        }
        isOpen={flatmateModel}
        onClose={() => setFlatmateModel(false)}
        llShowOnly={true}
        usedFor={"tenantListing"}
      />
      <NewGuarantorsModal
        isOpen={guarantorModel}
        user={user}
        profileId={profile?._id}
        guarantorsList={profile?.tenantid?.gurantor_id ?? []}
        onClose={() => setGuarantorModel(false)}
      />
      <ServingPromptModal
        openModal={servingPromptModal}
        profile={profile}
        URL={
          // user?.isParentLL
          //   ? `/${workspaceId}/profile-property?agencylink=${profile?._id}`
          //   :
          `/${workspaceId}/profile-property?sharedprofileid=${profile?._id}`
        }
        setOpenModal={setServingPromptModal}
      />
    </>
  );
};

export default ProfileRow;
